// S-10..S-14 `/survey/step-{1..5}` — анкета.
// Canonical fields come from frontend/src/api/schemas.ts §survey and backend allow-list.

import { forwardRef, useCallback, useState, useId, type KeyboardEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Check, Loader2, X } from 'lucide-react';
import { useLocation } from 'wouter';
import {
  Step1BasicSchema,
  Step2LifestyleSchema,
  Step3SymptomsSchema,
  Step4HistorySchema,
  Step5MedicationsSchema,
  type Step1Form,
  type Step2Form,
  type Step3Form,
  type Step4Form,
  type Step5Form,
} from '@/api/schemas';
import { usePatchSurveyStep } from '@/api/hooks/survey';
import { useProfileMe } from '@/api/hooks/profile';
import { applyValidationErrors, useApiErrorToast } from '@/api/error';
import type { ProfileMeResponse, Survey } from '@/api/types';
import { useSurveyContext } from '@/context/SurveyContext';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { StepHeader } from '@/components/survey/StepHeader';
import { FormField } from '@/components/forms/FormField';
import { DateField } from '@/components/forms/MaskedDateInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  chronicSuggestions,
  familySuggestions,
  symptomSuggestions,
} from '@/content/survey-suggestions';
import { firstIncompleteStepNumber, type SurveyStepNumber } from '@/lib/survey-progress';
import { cn } from '@/lib/utils';

type Option = { value: string; label: string; hint?: string };

const sexOptions: Option[] = [
  { value: 'female', label: 'Женский' },
  { value: 'male', label: 'Мужской' },
  { value: 'other', label: 'Другое' },
];

const activityOptions: Option[] = [
  { value: 'sedentary', label: 'Мало двигаюсь', hint: 'В основном сидячий день' },
  { value: 'light', label: 'Лёгкая активность', hint: 'Прогулки, бытовая активность' },
  { value: 'moderate', label: 'Умеренная', hint: 'Тренировки или ходьба 3-4 раза в неделю' },
  { value: 'high', label: 'Высокая', hint: 'Регулярные интенсивные нагрузки' },
];

const stressOptions: Option[] = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
];

const smokingOptions: Option[] = [
  { value: 'never', label: 'Никогда' },
  { value: 'past', label: 'Бросил(а)' },
  { value: 'current', label: 'Курю сейчас' },
];

const alcoholOptions: Option[] = [
  { value: 'never', label: 'Не употребляю' },
  { value: 'rare', label: 'Редко' },
  { value: 'weekly', label: 'Еженедельно' },
  { value: 'daily', label: 'Ежедневно' },
];

const step1FieldMap: Partial<Record<string, keyof Step1Form>> = {
  'step.sex': 'sex',
  'step.date_of_birth': 'date_of_birth',
  'step.height_cm': 'height_cm',
  'step.weight_kg': 'weight_kg',
};

const step2FieldMap: Partial<Record<string, keyof Step2Form>> = {
  'step.activity_level': 'activity_level',
  'step.sleep_hours_avg': 'sleep_hours_avg',
  'step.stress_level': 'stress_level',
  'step.smoking': 'smoking',
  'step.alcohol': 'alcohol',
  'step.free_text': 'free_text',
};

const step3FieldMap: Partial<Record<string, keyof Step3Form>> = {
  'step.symptoms': 'symptoms',
  'step.blood_pressure_issue': 'blood_pressure_issue',
  'step.sleep_issue': 'sleep_issue',
  'step.energy_issue': 'energy_issue',
  'step.weight_issue': 'weight_issue',
  'step.free_text': 'free_text',
};

const step4FieldMap: Partial<Record<string, keyof Step4Form>> = {
  'step.chronic_diagnoses': 'chronic_diagnoses',
  'step.family_history': 'family_history',
  'step.surgeries': 'surgeries',
  'step.free_text': 'free_text',
};

const step5FieldMap: Partial<Record<string, keyof Step5Form>> = {
  'step.medications_text': 'medications_text',
  'step.allergies_text': 'allergies_text',
};

function surveyFromProfile(profile?: ProfileMeResponse): Survey | null {
  if (!profile?.survey.exists) return null;
  return profile.survey;
}

function parseDecimalOrUndefined(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const normalized = value.trim().replace(',', '.');
  if (normalized === '') return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : value;
}

function firstIncompletePath(survey: Survey): string {
  const step = firstIncompleteStepNumber(survey);
  return step === null ? '/survey/step-5' : `/survey/step-${step}`;
}

function nextStepPath(step: SurveyStepNumber): string {
  return step < 5 ? `/survey/step-${step + 1}` : '/';   // Sprint 5A.1 — HomeEmpty inline на /
}

function useSubmitSuccess(step: SurveyStepNumber) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  return useCallback((survey: Survey) => {
    // На шагах 1..4 всегда продолжаем последовательно, не смотрим на survey.completed.
    // Mini-sprint 6-profile fix (2026-06-11): в edit-flow с Профиля backend возвращает
    // `completed=true` уже после step-1 PATCH (остальные шаги заполнены) — раньше это
    // давало ранний редирект на `/` и закрывало анкету на первом «Далее».
    // Онбординг от этого не страдает: в линейном flow 1→2→3→4→5 completed становится
    // true только после step-5 PATCH, который уходит в ветку ниже.
    if (step < 5) {
      setLocation(nextStepPath(step));
      return;
    }

    if (survey.completed) {
      setLocation('/');   // Sprint 5A.1 — HomeEmpty inline на /
      return;
    }

    toast({
      title: 'Анкета ещё не завершена',
      description: 'Проверьте предыдущие шаги: один из них не сохранён полностью.',
      variant: 'warning',
    });
    setLocation(firstIncompletePath(survey));
  }, [setLocation, step, toast]);
}

function SurveyIntro({ text }: { text: string }) {
  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      {text}
    </p>
  );
}

function SelectField({
  label,
  placeholder,
  value,
  onValueChange,
  options,
  error,
}: {
  label: string;
  placeholder: string;
  value?: string;
  onValueChange: (value: string) => void;
  options: Option[];
  error?: string;
}) {
  const id = useId();

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id={id}
          className={cn('h-10', error && 'border-destructive focus:ring-destructive')}
          aria-invalid={!!error}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function RadioCards({
  value,
  onValueChange,
  options,
  error,
}: {
  value?: string;
  onValueChange: (value: string) => void;
  options: Option[];
  error?: string;
}) {
  return (
    <div className="space-y-2">
      <RadioGroup value={value} onValueChange={onValueChange} className="gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-control border bg-card p-3 transition-colors',
              value === option.value ? 'border-primary/60 bg-primary/5' : 'border-border',
              error && 'border-destructive/50',
            )}
          >
            <RadioGroupItem value={option.value} className="mt-0.5" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">{option.label}</span>
              {option.hint && (
                <span className="mt-0.5 block text-hl-small leading-snug text-muted-foreground">
                  {option.hint}
                </span>
              )}
            </span>
          </label>
        ))}
      </RadioGroup>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

const TextareaField = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    label: string;
    error?: string;
  }
>(function TextareaField(
  { label, error, className, 'aria-describedby': ariaDescribedBy, ...props },
  ref,
) {
  const id = useId();
  const errorId = `${id}-err`;
  const describedBy = error ? errorId : ariaDescribedBy;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <Textarea
        id={id}
        ref={ref}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(
          'min-h-[88px] resize-none text-sm',
          error && 'border-destructive focus-visible:ring-destructive',
          className,
        )}
        {...props}
      />
      {error && (
        <p id={errorId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
});

// Экспортирован ради HEAL-54-теста (черновик не должен теряться) — рендерить весь
// шаг анкеты ради одного поля дороже, чем открыть компонент.
export function TagInput({
  label,
  hint,
  value,
  onChange,
  suggestions,
  placeholder,
  maxItems,
  error,
}: {
  label: string;
  hint?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  suggestions?: string[];
  placeholder: string;
  maxItems: number;
  error?: string;
}) {
  const [draft, setDraft] = useState('');
  const items = value ?? [];
  const canAddMore = items.length < maxItems;

  // HEAL-73: «Головные боли» и «головные боли» — один симптом, а не два.
  //
  // Раньше добавление сверялось без учёта регистра, а подсказка — с учётом: тот,
  // кто печатал уже выбранный симптом с маленькой буквы, получал его и зелёной
  // кнопкой сверху, и отдельным тегом снизу. Сравнение теперь одно на всё.
  const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

  const isActive = (candidate: string) => items.some((item) => same(item, candidate));

  const add = (raw: string) => {
    const next = raw.trim();
    if (!next || !canAddMore || isActive(next)) {
      setDraft('');
      return;
    }
    onChange([...items, next]);
    setDraft('');
  };

  const remove = (target: string) => {
    onChange(items.filter((item) => !same(item, target)));
  };

  const toggleSuggestion = (suggestion: string) => {
    if (isActive(suggestion)) {
      remove(suggestion);
      return;
    }
    add(suggestion);
  };

  // HEAL-73: снизу — только то, чего нет в подсказках.
  //
  // Выбранная подсказка уже подсвечена зелёным на своём месте; дублировать её
  // отдельным тегом значит показать один симптом дважды. Человек читал это так,
  // будто добавил его два раза.
  const ownItems = items.filter((item) => !(suggestions ?? []).some((s) => same(s, item)));

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add(draft);
  };

  // HEAL-54: набранный, но не «добавленный» текст молча пропадал — пользователь
  // печатал симптом и уходил на следующий шаг, считая, что сохранил его. Теперь
  // черновик коммитится на blur: тап по «Далее» сначала снимает фокус с поля,
  // значение успевает попасть в форму, и только потом уходит submit.
  const handleBlur = () => {
    if (draft.trim()) add(draft);
  };

  return (
    <div className="space-y-2">
      <div>
        <Label className="text-sm font-medium">{label}</Label>
        {hint && <p className="mt-1 text-hl-small leading-snug text-muted-foreground">{hint}</p>}
      </div>

      {suggestions && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((suggestion) => {
            const active = isActive(suggestion);
            return (
              <button
                key={suggestion}
                type="button"
                aria-pressed={active}
                onClick={() => toggleSuggestion(suggestion)}
                className={cn(
                  'rounded-md border px-2.5 py-1.5 text-hl-small font-medium transition-colors',
                  active
                    ? 'border-primary/60 bg-primary/10 text-primary-text'
                    : 'border-border bg-background text-foreground',
                )}
              >
                {active && <Check className="mr-1 inline h-3 w-3" />}
                {suggestion}
              </button>
            );
          })}
        </div>
      )}

      {ownItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {ownItems.map((item) => (
            // HEAL-73: свой симптом — такой же добавленный, как выбранная подсказка,
            // и выглядит так же. Серый тег читался как «неактивно, не сохранилось» —
            // человек нажимал «Добавить» повторно.
            //
            // Снимается тег целиком, а не только крестиком (круг ревью, находка 2).
            // Выглядит он теперь ровно как выбранная подсказка, а та снимается
            // нажатием в любое место. Оставить кликабельными 12 пикселей крестика
            // значило бы дать двум одинаковым на вид элементам разное поведение:
            // человек жмёт по тегу, ничего не происходит, и он решает, что симптом
            // удалить нельзя. Крестик остаётся — он объясняет, что будет по нажатию.
            <button
              key={item}
              type="button"
              onClick={() => remove(item)}
              aria-label={`Удалить ${item}`}
              className="inline-flex max-w-full items-center gap-1 rounded-md border border-primary/60 bg-primary/10 px-2.5 py-1 text-hl-small font-medium text-primary-text"
            >
              <span className="min-w-0 truncate">{item}</span>
              <X className="h-3 w-3 flex-shrink-0 text-primary/70" />
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={!canAddMore}
          className={cn('h-10 text-sm', error && 'border-destructive focus-visible:ring-destructive')}
        />
        {/* HEAL-54: была иконка «+». Она читалась как «добавить ещё одно поле», а не
            как «сохранить то, что я уже написал», — люди уходили дальше, не сохранив.
            Действие названо словом. */}
        <Button
          type="button"
          variant="outline"
          disabled={!draft.trim() || !canAddMore}
          onClick={() => add(draft)}
          className="h-10 flex-shrink-0 px-4 text-sm"
        >
          Добавить
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function IssueCheckbox({
  checked,
  onCheckedChange,
  label,
  hint,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer gap-3 rounded-control border bg-card p-3 transition-colors',
        checked ? 'border-primary/60 bg-primary/5' : 'border-border',
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        className="mt-0.5"
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="mt-0.5 block text-hl-small leading-snug text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function SurveyActions({
  isPending,
  onBack,
  isLast = false,
}: {
  isPending: boolean;
  onBack: () => void;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-3 pt-2">
      <Button type="button" variant="outline" className="h-10 flex-1" onClick={onBack}>
        Назад
      </Button>
      <Button type="submit" className="h-10 flex-1 text-sm font-semibold" disabled={isPending}>
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {isLast ? 'Готово' : 'Далее'}
        {!isPending && !isLast ? <ArrowRight className="h-4 w-4" /> : null}
      </Button>
    </div>
  );
}

export function SurveyStep1() {
  const profileQuery = useProfileMe();
  const surveyContext = useSurveyContext();
  const mutation = usePatchSurveyStep(1);
  const [, setLocation] = useLocation();
  const showErrorToast = useApiErrorToast();
  const submitSuccess = useSubmitSuccess(1);
  const persisted = surveyFromProfile(profileQuery.data)?.step1_basic ?? undefined;

  const {
    control,
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Step1Form>({
    resolver: zodResolver(Step1BasicSchema),
    defaultValues: surveyContext.drafts.step1 ?? persisted,
  });

  const goBack = () => {
    if (surveyContext.drafts.step1 !== undefined || isDirty) {
      surveyContext.saveDraft(1, getValues());
    }
    // Mini-sprint 6-profile fix (2026-06-11): intent-based выбор, не history API.
    // Шаги 2-5 используют setLocation('/survey/step-{n-1}') push'и для «Назад»,
    // поэтому history.back() здесь pop'ал бы на step-2 (последний навигационный
    // push), а не на исходную страницу. Используем survey.completed как domain
    // signal: completed=true → юзер редактирует с Профиля → возврат на /profile.
    // false → онбординг → возврат на /onboarding/intro.
    const surveyEmbed = profileQuery.data?.survey;
    const isEditFlow = Boolean(surveyEmbed?.exists && surveyEmbed.completed);
    setLocation(isEditFlow ? '/profile' : '/onboarding/intro');
  };

  const onSubmit = handleSubmit(async (payload) => {
    try {
      const response = await mutation.mutateAsync({ step: payload });
      surveyContext.clearDraft(1);
      submitSuccess(response.survey);
    } catch (err) {
      const applied = applyValidationErrors<keyof Step1Form>(err, setError, step1FieldMap);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <StepHeader step={1} title="Основные данные" onBack={goBack} />
      <main className="flex-1 overflow-y-auto px-6 pb-8 pt-1">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <SurveyIntro text="Укажите базовые параметры: они нужны для корректной интерпретации будущих анализов." />

          <Controller
            control={control}
            name="sex"
            render={({ field }) => (
              <SelectField
                label="Пол"
                placeholder="Выберите пол"
                value={field.value}
                onValueChange={field.onChange}
                options={sexOptions}
                error={errors.sex?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="date_of_birth"
            render={({ field }) => (
              <DateField
                label="Дата рождения"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.date_of_birth?.message}
              />
            )}
          />
          <FormField
            label="Рост (см)"
            type="number"
            inputMode="numeric"
            min={80}
            max={250}
            error={errors.height_cm?.message}
            {...register('height_cm', { setValueAs: parseDecimalOrUndefined })}
          />
          <FormField
            label="Вес (кг)"
            type="number"
            inputMode="decimal"
            min={30}
            max={300}
            step="0.01"
            error={errors.weight_kg?.message}
            {...register('weight_kg', { setValueAs: parseDecimalOrUndefined })}
          />

          <SurveyActions isPending={mutation.isPending} onBack={goBack} />
        </form>
      </main>
    </MobileFrame>
  );
}

export function SurveyStep2() {
  const profileQuery = useProfileMe();
  const surveyContext = useSurveyContext();
  const mutation = usePatchSurveyStep(2);
  const [, setLocation] = useLocation();
  const showErrorToast = useApiErrorToast();
  const submitSuccess = useSubmitSuccess(2);
  const persisted = surveyFromProfile(profileQuery.data)?.step2_lifestyle;
  const defaultValues: Partial<Step2Form> = surveyContext.drafts.step2 ?? {
    activity_level: persisted?.activity_level,
    sleep_hours_avg: persisted?.sleep_hours_avg,
    stress_level: persisted?.stress_level,
    smoking: persisted?.smoking,
    alcohol: persisted?.alcohol,
    free_text: persisted?.free_text ?? '',
  };

  const {
    control,
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Step2Form>({
    resolver: zodResolver(Step2LifestyleSchema),
    defaultValues,
  });

  const goBack = () => {
    if (surveyContext.drafts.step2 !== undefined || isDirty) {
      surveyContext.saveDraft(2, getValues());
    }
    setLocation('/survey/step-1');
  };

  const onSubmit = handleSubmit(async (payload) => {
    try {
      const response = await mutation.mutateAsync({ step: payload });
      surveyContext.clearDraft(2);
      submitSuccess(response.survey);
    } catch (err) {
      const applied = applyValidationErrors<keyof Step2Form>(err, setError, step2FieldMap);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <StepHeader step={2} title="Образ жизни" onBack={goBack} />
      <main className="flex-1 overflow-y-auto px-6 pb-8 pt-1">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <SurveyIntro text="Эти ответы помогают отделить влияние привычек от медицинских показателей." />

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Уровень активности</Label>
            <Controller
              control={control}
              name="activity_level"
              render={({ field }) => (
                <RadioCards
                  value={field.value}
                  onValueChange={field.onChange}
                  options={activityOptions}
                  error={errors.activity_level?.message}
                />
              )}
            />
          </div>

          <FormField
            label="Сколько часов вы обычно спите?"
            type="text"
            inputMode="decimal"
            placeholder="Например: 7.5"
            hint="Можно указать дробное значение: 6,5 или 6.5"
            error={errors.sleep_hours_avg?.message}
            {...register('sleep_hours_avg', { setValueAs: parseDecimalOrUndefined })}
          />

          <Controller
            control={control}
            name="stress_level"
            render={({ field }) => (
              <SelectField
                label="Уровень стресса"
                placeholder="Выберите уровень"
                value={field.value}
                onValueChange={field.onChange}
                options={stressOptions}
                error={errors.stress_level?.message}
              />
            )}
          />

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Курение</Label>
            <Controller
              control={control}
              name="smoking"
              render={({ field }) => (
                <RadioCards
                  value={field.value}
                  onValueChange={field.onChange}
                  options={smokingOptions}
                  error={errors.smoking?.message}
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Алкоголь</Label>
            <Controller
              control={control}
              name="alcohol"
              render={({ field }) => (
                <RadioCards
                  value={field.value}
                  onValueChange={field.onChange}
                  options={alcoholOptions}
                  error={errors.alcohol?.message}
                />
              )}
            />
          </div>

          <TextareaField
            label="Что ещё важно знать? (по желанию)"
            placeholder="Например: сменный график, восстановление после болезни, частые перелёты"
            rows={3}
            error={errors.free_text?.message}
            {...register('free_text')}
          />

          <SurveyActions isPending={mutation.isPending} onBack={goBack} />
        </form>
      </main>
    </MobileFrame>
  );
}

export function SurveyStep3() {
  const profileQuery = useProfileMe();
  const surveyContext = useSurveyContext();
  const mutation = usePatchSurveyStep(3);
  const [, setLocation] = useLocation();
  const showErrorToast = useApiErrorToast();
  const submitSuccess = useSubmitSuccess(3);
  const persisted = surveyFromProfile(profileQuery.data)?.step3_symptoms;
  const defaultValues: Step3Form = surveyContext.drafts.step3 ?? {
    symptoms: persisted?.symptoms ?? [],
    blood_pressure_issue: persisted?.blood_pressure_issue ?? false,
    sleep_issue: persisted?.sleep_issue ?? false,
    energy_issue: persisted?.energy_issue ?? false,
    weight_issue: persisted?.weight_issue ?? false,
    free_text: persisted?.free_text ?? '',
  };

  const {
    control,
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Step3Form>({
    resolver: zodResolver(Step3SymptomsSchema),
    defaultValues,
  });

  const goBack = () => {
    if (surveyContext.drafts.step3 !== undefined || isDirty) {
      surveyContext.saveDraft(3, getValues());
    }
    setLocation('/survey/step-2');
  };

  const onSubmit = handleSubmit(async (payload) => {
    try {
      const response = await mutation.mutateAsync({ step: payload });
      surveyContext.clearDraft(3);
      submitSuccess(response.survey);
    } catch (err) {
      const applied = applyValidationErrors<keyof Step3Form>(err, setError, step3FieldMap);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <StepHeader step={3} title="Самочувствие" onBack={goBack} />
      <main className="flex-1 overflow-y-auto px-6 pb-8 pt-1">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <SurveyIntro text="Отметьте регулярные состояния и добавьте свои симптомы, если их нет в подсказках." />

          <Controller
            control={control}
            name="symptoms"
            render={({ field }) => (
              <TagInput
                label="Симптомы"
                hint="Можно выбрать подсказки или добавить свой вариант"
                value={field.value}
                onChange={field.onChange}
                suggestions={symptomSuggestions}
                placeholder="Добавить симптом"
                maxItems={50}
                error={errors.symptoms?.message}
              />
            )}
          />

          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Что встречается регулярно?</Label>
              <p className="mt-1 text-hl-small leading-snug text-muted-foreground">
                Отметьте всё, что заметно повторяется в обычной жизни.
              </p>
            </div>
            <Controller
              control={control}
              name="blood_pressure_issue"
              render={({ field }) => (
                <IssueCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  label="Давление"
                  hint="Повышенное, пониженное или заметные скачки"
                />
              )}
            />
            <Controller
              control={control}
              name="sleep_issue"
              render={({ field }) => (
                <IssueCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  label="Сон"
                  hint="Трудно заснуть, частые пробуждения или невысыпание"
                />
              )}
            />
            <Controller
              control={control}
              name="energy_issue"
              render={({ field }) => (
                <IssueCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  label="Энергия"
                  hint="Усталость, упадок сил или тяжёлое восстановление"
                />
              )}
            />
            <Controller
              control={control}
              name="weight_issue"
              render={({ field }) => (
                <IssueCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  label="Вес"
                  hint="Набор, снижение или трудности с контролем веса"
                />
              )}
            />
          </div>

          <TextareaField
            label="Комментарий (по желанию)"
            placeholder="Например: когда началось, как часто повторяется"
            rows={3}
            error={errors.free_text?.message}
            {...register('free_text')}
          />

          <SurveyActions isPending={mutation.isPending} onBack={goBack} />
        </form>
      </main>
    </MobileFrame>
  );
}

export function SurveyStep4() {
  const profileQuery = useProfileMe();
  const surveyContext = useSurveyContext();
  const mutation = usePatchSurveyStep(4);
  const [, setLocation] = useLocation();
  const showErrorToast = useApiErrorToast();
  const submitSuccess = useSubmitSuccess(4);
  const persisted = surveyFromProfile(profileQuery.data)?.step4_history;
  const defaultValues: Step4Form = surveyContext.drafts.step4 ?? {
    chronic_diagnoses: persisted?.chronic_diagnoses ?? [],
    family_history: persisted?.family_history ?? [],
    surgeries: persisted?.surgeries ?? '',
    free_text: persisted?.free_text ?? '',
  };

  const {
    control,
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Step4Form>({
    resolver: zodResolver(Step4HistorySchema),
    defaultValues,
  });

  const goBack = () => {
    if (surveyContext.drafts.step4 !== undefined || isDirty) {
      surveyContext.saveDraft(4, getValues());
    }
    setLocation('/survey/step-3');
  };

  const onSubmit = handleSubmit(async (payload) => {
    try {
      const response = await mutation.mutateAsync({ step: payload });
      surveyContext.clearDraft(4);
      submitSuccess(response.survey);
    } catch (err) {
      const applied = applyValidationErrors<keyof Step4Form>(err, setError, step4FieldMap);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <StepHeader step={4} title="История здоровья" onBack={goBack} />
      <main className="flex-1 overflow-y-auto px-6 pb-8 pt-1">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <SurveyIntro text="Вспомните хронические состояния, семейную историю и операции: это помогает точнее учитывать риски." />

          <Controller
            control={control}
            name="chronic_diagnoses"
            render={({ field }) => (
              <TagInput
                label="Хронические диагнозы"
                hint="Если ничего нет, оставьте список пустым"
                value={field.value}
                onChange={field.onChange}
                suggestions={chronicSuggestions}
                placeholder="Добавить диагноз или состояние"
                maxItems={30}
                error={errors.chronic_diagnoses?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="family_history"
            render={({ field }) => (
              <TagInput
                label="Семейная история"
                hint="Болезни у близких родственников, если знаете"
                value={field.value}
                onChange={field.onChange}
                suggestions={familySuggestions}
                placeholder="Добавить состояние"
                maxItems={30}
                error={errors.family_history?.message}
              />
            )}
          />

          <TextareaField
            label="Операции и госпитализации"
            placeholder="Например: аппендэктомия в 2018, госпитализаций не было"
            rows={4}
            error={errors.surgeries?.message}
            {...register('surgeries')}
          />

          <TextareaField
            label="Комментарий (по желанию)"
            placeholder="Любые детали, которые могут быть важны врачу или AI-интерпретации"
            rows={3}
            error={errors.free_text?.message}
            {...register('free_text')}
          />

          <SurveyActions isPending={mutation.isPending} onBack={goBack} />
        </form>
      </main>
    </MobileFrame>
  );
}

export function SurveyStep5() {
  const profileQuery = useProfileMe();
  const surveyContext = useSurveyContext();
  const mutation = usePatchSurveyStep(5);
  const [, setLocation] = useLocation();
  const showErrorToast = useApiErrorToast();
  const submitSuccess = useSubmitSuccess(5);
  const persisted = surveyFromProfile(profileQuery.data)?.step5_medications;
  // Источник истины для defaultValues: persisted из API или draft из контекста.
  // ВАЖНО — merge через spread, а не `??`. Если drafts.step5 окажется partial (только одно поле),
  // второе поле всё равно получит default. Иначе RHF state не зарегистрирует ключ и Controller
  // придёт с value=undefined → DOM не синхронизирован с state.
  const baseDefault = {
    medications_text: persisted?.medications_text ?? '',
    allergies_text: persisted?.allergies_text ?? '',
  };
  const defaultValues: Step5Form = {
    ...baseDefault,
    ...(surveyContext.drafts.step5 ?? {}),
  };

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Step5Form>({
    resolver: zodResolver(Step5MedicationsSchema),
    defaultValues,
  });

  const goBack = () => {
    if (surveyContext.drafts.step5 !== undefined || isDirty) {
      surveyContext.saveDraft(5, getValues());
    }
    setLocation('/survey/step-4');
  };

  const onSubmit = handleSubmit(async (payload) => {
    try {
      const response = await mutation.mutateAsync({ step: payload });
      surveyContext.clearDraft(5);
      submitSuccess(response.survey);
    } catch (err) {
      const applied = applyValidationErrors<keyof Step5Form>(err, setError, step5FieldMap);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <StepHeader step={5} title="Медикаменты и аллергии" onBack={goBack} />
      <main className="flex-1 overflow-y-auto px-6 pb-8 pt-1">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <SurveyIntro text="Финальный шаг: перечислите лекарства, добавки и известные аллергии. Если ничего нет, оставьте поля пустыми и отправьте шаг." />

          {/* Controlled через Controller — value/onChange всегда синхронизированы с RHF state.
              Раньше register() оставлял textarea uncontrolled: на iOS Safari Cyrillic IME composition
              мог не закрыться до tap по «Готово», onChange не выстреливал, RHF state оставался пустым.
              prod-инцидент 25.05.2026: фронт слал {step:{}} (потеря данных), бэк падал в 500. */}
          <Controller
            control={control}
            name="medications_text"
            render={({ field }) => (
              <TextareaField
                label="Лекарства и добавки"
                placeholder="Например: витамин D 2000 МЕ, магний; регулярных лекарств нет"
                rows={5}
                error={errors.medications_text?.message}
                name={field.name}
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />

          <Controller
            control={control}
            name="allergies_text"
            render={({ field }) => (
              <TextareaField
                label="Аллергии"
                placeholder="Например: пенициллин, пыльца; аллергий не знаю"
                rows={4}
                error={errors.allergies_text?.message}
                name={field.name}
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />

          <SurveyActions isPending={mutation.isPending} onBack={goBack} isLast />
        </form>
      </main>
    </MobileFrame>
  );
}
